import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { useModal } from '../../context/ModalContext';
import { FiArrowLeft, FiSave, FiEye, FiCheckSquare, FiSquare, FiRefreshCcw, FiTrash2 } from 'react-icons/fi';

function TenantDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { alert, confirm } = useModal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [tenantNotes, setTenantNotes] = useState('');
  const [availableModules, setAvailableModules] = useState([]);
  const [enabledModules, setEnabledModules] = useState([]);
  const [kitchenMode, setKitchenMode] = useState('kds');
  const [kotPrintMode, setKotPrintMode] = useState('manual');
  const [savingKitchenMode, setSavingKitchenMode] = useState(false);
  const [qrFormSettings, setQrFormSettings] = useState({
    allowSkip: true // Default: allow skip (show cancel button)
  });
  const [savingQrSettings, setSavingQrSettings] = useState(false);
  const [billDeletePassword, setBillDeletePassword] = useState('');
  const [isBillDeletePasswordSet, setIsBillDeletePasswordSet] = useState(false);
  const [savingBillDeletePassword, setSavingBillDeletePassword] = useState(false);

  useEffect(() => {
    fetchTenant();
    fetchAvailableModules();
  }, [id]);

  const fetchTenant = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/owner/tenants/${id}`);
      const tenantData = response.data.tenant;
      const modules = (tenantData.tenant_modules || [])
        .filter((m) => m.is_enabled)
        .map((m) => m.module_name);
      setTenant(tenantData);
      setTenantNotes(tenantData.notes || '');
      setKitchenMode(tenantData.kitchen_mode === 'kot' ? 'kot' : 'kds');
      setKotPrintMode(tenantData.kot_print_mode === 'auto' ? 'auto' : 'manual');
      setIsBillDeletePasswordSet(Boolean(tenantData.bill_delete_password_set));
      setBillDeletePassword('');
      setEnabledModules(modules);
      
      // Fetch QR form settings (owner context continues to use current tenant access mode)
      try {
        const settingsResponse = await api.get('/api/settings');
        const allowSkip = settingsResponse.data?.qr_menu_form_allow_skip !== 'false';
        setQrFormSettings({ allowSkip });
      } catch (settingsError) {
        console.error('Failed to fetch QR form settings:', settingsError);
      }
    } catch (error) {
      await alert('Failed to load tenant details', 'Error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableModules = async () => {
    try {
      const response = await api.get('/api/owner/modules');
      setAvailableModules(response.data.modules || []);
    } catch (error) {
      // ignore
    }
  };

  const toggleModule = (moduleName) => {
    setEnabledModules((prev) =>
      prev.includes(moduleName)
        ? prev.filter((m) => m !== moduleName)
        : [...prev, moduleName]
    );
  };

  const handleSaveModules = async () => {
    try {
      setSaving(true);
      await api.put(`/api/owner/tenants/${id}/modules`, {
        modules: enabledModules
      });
      await alert('Modules updated successfully', 'Success', 'success');
      fetchTenant();
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to update modules', 'Error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveKitchenMode = async () => {
    try {
      setSavingKitchenMode(true);
      await api.put(`/api/owner/tenants/${id}/kitchen-mode`, {
        kitchen_mode: kitchenMode,
        kot_print_mode: kotPrintMode
      });
      await alert('Kitchen mode updated successfully', 'Success', 'success');
      fetchTenant();
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to update kitchen mode', 'Error', 'error');
    } finally {
      setSavingKitchenMode(false);
    }
  };

  const handleAccessTenant = () => {
    if (!tenant) return;
    localStorage.setItem('ownerTenantId', tenant.id);
    localStorage.setItem('ownerTenantName', tenant.name || 'Tenant');
    navigate('/restaurant/dashboard');
  };

  const handleSaveQrFormSettings = async () => {
    try {
      setSavingQrSettings(true);
      await api.put('/api/settings', {
        qr_menu_form_allow_skip: qrFormSettings.allowSkip ? 'true' : 'false'
      });
      await alert('QR Menu form settings updated successfully', 'Success', 'success');
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to update QR form settings', 'Error', 'error');
    } finally {
      setSavingQrSettings(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setSaving(true);
      await api.put(`/api/owner/tenants/${id}`, {
        notes: tenantNotes
      });
      await alert('Tenant notes updated successfully', 'Success', 'success');
      fetchTenant();
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to update tenant notes', 'Error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBillDeletePassword = async () => {
    const trimmedPassword = String(billDeletePassword || '').trim();
    if (!trimmedPassword || trimmedPassword.length < 6) {
      await alert('Bill delete password must be at least 6 characters long', 'Validation Error', 'warning');
      return;
    }

    try {
      setSavingBillDeletePassword(true);
      await api.put(`/api/owner/tenants/${id}/bill-delete-password`, {
        billDeletePassword: trimmedPassword
      });
      setIsBillDeletePasswordSet(true);
      setBillDeletePassword('');
      await alert('Bill delete password updated successfully', 'Success', 'success');
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to update bill delete password', 'Error', 'error');
    } finally {
      setSavingBillDeletePassword(false);
    }
  };

  const handleResetPassword = async () => {
    const newPassword = window.prompt('Enter a new password for the tenant admin (min 6 chars):');
    if (!newPassword) return;
    if (newPassword.length < 6) {
      await alert('Password must be at least 6 characters long', 'Validation Error', 'warning');
      return;
    }
    try {
      await api.post(`/api/owner/tenants/${id}/reset-password`, { newPassword });
      await alert('Tenant admin password reset successfully', 'Success', 'success');
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to reset password', 'Error', 'error');
    }
  };

  const handleEditUserPassword = async (userId, userEmail) => {
    const newPassword = window.prompt(`Enter a new password for ${userEmail} (min 6 chars):`);
    if (!newPassword) return;
    if (newPassword.length < 6) {
      await alert('Password must be at least 6 characters long', 'Validation Error', 'warning');
      return;
    }
    try {
      await api.post(`/api/owner/tenants/${id}/reset-password`, { newPassword, userId });
      await alert('User password updated successfully', 'Success', 'success');
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to update password', 'Error', 'error');
    }
  };

  const handleDeleteTenant = async () => {
    const confirmed = await confirm(
      'This will permanently delete the tenant and all its data. Continue?',
      'Delete Tenant'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/api/owner/tenants/${id}`);
      if (localStorage.getItem('ownerTenantId') === id) {
        localStorage.removeItem('ownerTenantId');
        localStorage.removeItem('ownerTenantName');
      }
      await alert('Tenant deleted successfully', 'Success', 'success');
      navigate('/owner/dashboard');
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to delete tenant', 'Error', 'error');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-10 text-gray-600">Loading tenant...</div>
      </Layout>
    );
  }

  if (!tenant) {
    return (
      <Layout>
        <div className="text-center py-10 text-gray-600">Tenant not found.</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8 space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/owner/dashboard')}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <FiArrowLeft /> Back
          </button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900">{tenant.name}</h1>
            <p className="text-gray-600">Tenant management and feature access</p>
          </div>
          <button
            onClick={handleAccessTenant}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
          >
            <FiEye /> Access Tenant
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-2">
            <h2 className="text-lg font-semibold text-gray-900">Tenant Info</h2>
            <div className="text-sm text-gray-600">Slug: {tenant.slug}</div>
            <div className="text-sm text-gray-600">Email: {tenant.email || 'N/A'}</div>
            <div className="text-sm text-gray-600">Phone: {tenant.phone || 'N/A'}</div>
            <div className="text-sm text-gray-600">Status: {tenant.is_active ? 'Active' : 'Inactive'}</div>
            <div className="text-sm text-gray-600">Max Staff: {tenant.max_staff_logins || 0}</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-2">
            <h2 className="text-lg font-semibold text-gray-900">Plan</h2>
            {tenant.tenant_plans?.[0] ? (
              <>
                <div className="text-sm text-gray-600">Name: {tenant.tenant_plans[0].plan_name}</div>
                <div className="text-sm text-gray-600">
                  Price: ₹{parseFloat(tenant.tenant_plans[0].plan_price || 0).toLocaleString('en-IN')}
                </div>
                <div className="text-sm text-gray-600">Cycle: {tenant.tenant_plans[0].billing_cycle}</div>
                <div className="text-sm text-gray-600">Status: {tenant.tenant_plans[0].status}</div>
                <div className="text-sm text-gray-600">
                  Start: {tenant.tenant_plans[0].subscription_starts_at ? new Date(tenant.tenant_plans[0].subscription_starts_at).toLocaleDateString('en-IN') : 'N/A'}
                </div>
                <div className="text-sm text-gray-600">
                  End: {tenant.tenant_plans[0].subscription_ends_at ? new Date(tenant.tenant_plans[0].subscription_ends_at).toLocaleDateString('en-IN') : 'N/A'}
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-600">No plan assigned</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Feature Access (Modules)</h2>
            <button
              onClick={handleSaveModules}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-60"
            >
              <FiSave /> {saving ? 'Saving...' : 'Save Modules'}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {availableModules.map((module) => {
              const enabled = enabledModules.includes(module);
              const moduleLabel = module === 'kds' && kitchenMode === 'kot'
                ? 'kot'
                : module;
              return (
                <button
                  key={module}
                  onClick={() => toggleModule(module)}
                  className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:border-gray-900 transition-colors text-left"
                >
                  {enabled ? <FiCheckSquare className="text-gray-900" /> : <FiSquare className="text-gray-400" />}
                  <span className="text-sm font-medium text-gray-700 capitalize">{moduleLabel.replace('_', ' ')}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Kitchen Workflow Mode</h2>
              <p className="text-sm text-gray-600 mt-1">Choose between screen-based KDS or print-based KOT for this tenant.</p>
            </div>
            <button
              onClick={handleSaveKitchenMode}
              disabled={savingKitchenMode}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-60"
            >
              <FiSave /> {savingKitchenMode ? 'Saving...' : 'Save'}
            </button>
          </div>
          <div>
            <select
              value={kitchenMode}
              onChange={(e) => setKitchenMode(e.target.value)}
              className="w-full md:w-80 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            >
              <option value="kds">KDS (Kitchen Display Screen)</option>
              <option value="kot">KOT (Kitchen Order Ticket)</option>
            </select>
          </div>

          {kitchenMode === 'kot' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">KOT Print Mode</label>
              <select
                value={kotPrintMode}
                onChange={(e) => setKotPrintMode(e.target.value)}
                className="w-full md:w-80 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              >
                <option value="auto">Auto KOT Print (one grouped KOT per order)</option>
                <option value="manual">Manual KOT (Print button in KOT screen)</option>
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Auto mode groups all items of the same order into a single KOT ticket and avoids duplicate multi-ticket printing.
              </p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Notes</h2>
            <button
              onClick={handleSaveNotes}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-60"
            >
              <FiSave /> {saving ? 'Saving...' : 'Save Notes'}
            </button>
          </div>
          <textarea
            value={tenantNotes}
            onChange={(e) => setTenantNotes(e.target.value)}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            placeholder="Add private owner notes for this tenant..."
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Bill Delete Password</h2>
              <p className="text-sm text-gray-600 mt-1">
                Set a tenant-specific password for deleting bills. This is separate from login passwords.
              </p>
            </div>
            <span
              className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                isBillDeletePasswordSet ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
              }`}
            >
              {isBillDeletePasswordSet ? 'Configured' : 'Not Set'}
            </span>
          </div>

          <div className="flex flex-col md:flex-row gap-3">
            <input
              type="password"
              value={billDeletePassword}
              onChange={(e) => setBillDeletePassword(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              placeholder="Enter new bill delete password (min 6 chars)"
            />
            <button
              onClick={handleSaveBillDeletePassword}
              disabled={savingBillDeletePassword}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-60"
            >
              <FiSave /> {savingBillDeletePassword ? 'Saving...' : 'Save Password'}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">QR Menu Form Settings</h2>
              <p className="text-sm text-gray-600 mt-1">Control whether customers can skip the personal details form</p>
            </div>
            <button
              onClick={handleSaveQrFormSettings}
              disabled={savingQrSettings}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-60"
            >
              <FiSave /> {savingQrSettings ? 'Saving...' : 'Save'}
            </button>
          </div>
          <div className="space-y-3">
            <label className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-gray-900 transition-colors">
              <input
                type="checkbox"
                checked={qrFormSettings.allowSkip}
                onChange={(e) => setQrFormSettings({ allowSkip: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">Allow customers to skip form</div>
                <div className="text-sm text-gray-600">
                  {qrFormSettings.allowSkip 
                    ? 'Customers can view menu without filling personal details (Cancel button shown)'
                    : 'Customers must fill personal details to view menu (No cancel button)'}
                </div>
              </div>
            </label>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-2">
          <h2 className="text-lg font-semibold text-gray-900">Users</h2>
          {(tenant.users || []).length > 0 ? (
            <div className="space-y-2">
              {tenant.users.map((user) => (
                <div key={user.id} className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-700">
                  <span>{user.name} ({user.email})</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">{user.role}</span>
                    <button
                      onClick={() => handleEditUserPassword(user.id, user.email)}
                      className="text-xs px-2 py-1 border border-gray-200 rounded hover:border-gray-900"
                    >
                      Edit Password
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-600">No users found.</div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Owner Actions</h2>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleResetPassword}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <FiRefreshCcw /> Reset Tenant Admin Password
            </button>
            <button
              onClick={handleDeleteTenant}
              className="inline-flex items-center gap-2 px-4 py-2 border border-red-200 text-red-700 rounded-lg hover:bg-red-50"
            >
              <FiTrash2 /> Delete Tenant
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default TenantDetails;
